import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginForm from "./components/LoginForm";
import RegistrationForm from "./components/RegistrationForm";
import { useNavigate, useLocation } from "react-router-dom";
import ThemeToggle from "./components/ThemeToggle";
import IncidentPage from "./pages/IncidentPage";
import LegalPage from "./pages/LegalPage";
import AuthRedirectHandler from "./components/AuthRedirectHandler";
import SubscriptionPage from "./components/SubscriptionPlans"; // Changed from SubscriptionPlans
import { getAuthApiToken } from "./api/tokenService";
import LandingPage from './components/LandingPage';
import LegalLandingPage from './components/LegalLandingPage';
import { APP_MODES } from './appMode';

// -------------------------------------------------------------------
// Main App (handles authentication and global state)
// -------------------------------------------------------------------
function MainApp() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { login, logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [apiUrl, setApiUrl] = useState();
  const [isInitializing, setIsInitializing] = useState(true);
  const hasAutoLoggedIn = useRef(false);
  const appMode = location.pathname === '/legal' ? APP_MODES.LEGAL : APP_MODES.INCIDENT;

  // Subscription state
  const [showSubscriptionPage, setShowSubscriptionPage] = useState(false); // Changed name
  const [currentSubscription, setCurrentSubscription] = useState(null);

  // Load API URL from localStorage or env
  useEffect(() => {
    console.log("🔧 Loading API URL...", process.env.REACT_APP_API_URL);
    const savedUrl = process.env.REACT_APP_API_URL; // || localStorage.getItem('apiUrl') || 'http://127.0.0.1:3000'
    setApiUrl(savedUrl);
  }, []);

  // Fetch current subscription when user is logged in
  useEffect(() => {
    if (user) {
      fetchCurrentSubscription();
    }
  }, [user]);

  const fetchCurrentSubscription = async () => {
    try {
      const authApiUrl = process.env.REACT_APP_AUTH_URL || apiUrl;
      const token = await getAuthApiToken();

      const response = await fetch(`${authApiUrl}/api/auth/subscription/my-subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentSubscription(data);
      } else {
        console.error('Failed to fetch subscription:', response.status);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  // ONLY handle auto-login for existing sessions (not redirects)
  useEffect(() => {
    // Skip if redirect is being handled by AuthRedirectHandler
    if (inProgress !== 'none') {
      setIsInitializing(true);
      return;
    }

    setIsInitializing(false);

    // Check if we just logged out
    const justLoggedOut = sessionStorage.getItem('justLoggedOut');
    if (justLoggedOut === 'true') {
      console.log("🚪 Just logged out, skipping auto-login");
      return;
    }

    // Only auto-login if we have accounts, no user, and haven't auto-logged in yet
    if (accounts.length > 0 && !user && !hasAutoLoggedIn.current) {
      hasAutoLoggedIn.current = true;
      const account = accounts[0];
      instance.setActiveAccount(account);

      instance.acquireTokenSilent({
        scopes: ["openid", "profile", "email"],
        account
      })
        .then(response => {
          // Store token for API calls
          login(account, response.idToken);
          navigate(window.location.pathname || "/");
        })
        .catch(err => {
          console.error("Token acquisition failed:", err);
          hasAutoLoggedIn.current = false;
          sessionStorage.setItem('needsManualLogin', 'true');
        });
    }
  }, [inProgress, accounts, user, instance, login, navigate]);

  // Logout handler
  const handleLogout = async () => {
    try {
      console.log("🚪 Logging out...");

      // Set flag to prevent auto-login
      sessionStorage.setItem('justLoggedOut', 'true');
      sessionStorage.setItem('logoutTime', Date.now().toString());

      // Clear local state
      logout();

      // Clear stored tokens
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('user');

      // Clear MSAL cache
      await instance.clearCache();
      instance.setActiveAccount(null);

      // Clear storage
      Object.keys(localStorage).forEach(key => {
        if (key.toLowerCase().includes('msal')) localStorage.removeItem(key);
      });
      Object.keys(sessionStorage).forEach(key => {
        if (key.toLowerCase().includes('msal')) sessionStorage.removeItem(key);
      });

      // Redirect to Microsoft logout (preserve the current entry path)
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + window.location.pathname,
      });
    } catch (err) {
      console.error('Logout error:', err);
      window.location.href = window.location.origin + window.location.pathname;
    }
  };

  // Show loading while MSAL is initializing or processing redirect
  if (isInitializing || inProgress !== 'none') {
    return (
      <div className="auth-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Completing login...</h2>
          <p>Please wait while we authenticate you</p>
        </div>
      </div>
    );
  }

  // If not authenticated, show login/registration
  if (!user) {
    return location.pathname === '/legal' ? <LegalLandingPage /> : <LandingPage />;
    // return (
    //   <div className="auth-container">
    //     <div className="auth-grid">
    //       <RegistrationForm />
    //       <div className="auth-divider"></div>
    //       <LoginForm />
    //     </div>
    //   </div>
    // );
  }

  // User is authenticated – render the appropriate page based on the configured mode.
  return (
    <div className="App">
      <header className="App-header">
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '15px' }}>
          {/* Left: action buttons */}
          <div style={{ flex: '0 0 auto', display: 'flex', gap: '10px' }}>
            <button onClick={handleLogout} className="logout-btn">Logout</button>
          </div>

          {/* Center: title */}
          <div style={{ flex: '1 1 auto', textAlign: 'center' }}>
            <h1 className="main-title">
              {appMode === APP_MODES.LEGAL ? '⚖️ Legal Case Assessment AI' : '🛡️ Incident Analysis AI'}
            </h1>
            <p className="subtitle">
              {appMode === APP_MODES.LEGAL
                ? 'AI-powered initial case analysis for lawyers'
                : 'AI-powered incident investigation and root cause analysis'}
            </p>
          </div>

          {/* Right: empty spacer */}
          <div style={{ flex: '0 0 auto', width: '100px' }}></div>
        </div>
      </header>

      <main className="App-main">
        {appMode === APP_MODES.LEGAL ? (
          <LegalPage apiUrl={apiUrl} idToken={user?.idToken} />
        ) : (
          <IncidentPage apiUrl={apiUrl} setApiUrl={setApiUrl} idToken={user?.idToken} />
        )}
      </main>

      <footer className="App-footer">
        <p>Connected to: {apiUrl}</p>
        {currentSubscription && (
          <p style={{ fontSize: '12px', marginTop: '5px' }}>
            {currentSubscription.plan?.name !== 'free' ? (
              <>Plan: {currentSubscription.plan?.displayName} |
                Expires: {new Date(currentSubscription.endDate).toLocaleDateString()}</>
            ) : (
              <>Free Plan - <button onClick={() => setShowSubscriptionPage(true)} style={{ background: 'none', border: 'none', color: '#7a7a7a', cursor: 'pointer', textDecoration: 'underline' }}>Upgrade</button></>
            )}
          </p>
        )}
      </footer>

      {/* Unified Subscription Page - Shows both current plan and available plans */}
      {showSubscriptionPage && (
        <SubscriptionPage onClose={() => setShowSubscriptionPage(false)} />
      )}
    </div>
  );
}

// -------------------------------------------------------------------
// Root App with AuthProvider
// -------------------------------------------------------------------
export default function App() {
  return (
    <AuthProvider>
      <ThemeToggle />
      <AuthRedirectHandler />
      <MainApp />
    </AuthProvider>
  );
}